import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Box, Button, InputAdornment, Paper, Skeleton } from "@mui/material";
import DataTable from "../../lib/components/DataTable";
import { FileDownload, Search } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import scrollHandler from "../../utils/scrollHandler";
import useDebounce from "../../hooks/useDebounce";
import organizationservice from "../../service/organizationservice";
import pathnames from "../../constants/pathnames";
import moment from "moment";
import ExportTransmissionModal from "./components/ExportTransmission";
import DateRangePreset, { buildDateParams, DATE_PRESETS, extractFilterDates, normalizeDatePreset } from "./components/DateRangePreset";
import SortFilterMenu from "./components/SortFilterMenu";

const SORT_BY_ITEMS = [
  { label: "Last Transmission", value: "lastTransmission" },
  { label: "Name", value: "name" },
];

const TransmissionApiPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFileTransmission = location.pathname === pathnames.TRANSMISSIONS;
  const isApiTransmission = location.pathname === pathnames.API_TRANSMISSIONS;

  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";
  const status = searchParams.get("status") || "";
  const urlDatePreset = searchParams.get("datePreset");
  const urlDateFrom = searchParams.get("dateFrom") || null;
  const urlDateTo = searchParams.get("dateTo") || null;
  const sortBy = searchParams.get("sortBy") || "lastTransmission";
  const sortOrder = searchParams.get("sortOrder") || "desc";
  const searchTerm = useDebounce(keyword);
  const [isExport, setIsExport] = React.useState(false);
  const [responseFilters, setResponseFilters] = React.useState(null);

  const datePreset = normalizeDatePreset(urlDatePreset || responseFilters?.datePreset || DATE_PRESETS.THIS_MONTH);
  const dateFrom = urlDateFrom || responseFilters?.dateFrom || null;
  const dateTo = urlDateTo || responseFilters?.dateTo || null;

  const queries = {
    page,
    limit,
    keyword: searchTerm,
    status,
    sortBy,
    sortOrder,
    mode: "api",
    // First load: omit date params so backend defaults to THIS_MONTH.
    ...(urlDatePreset ? buildDateParams(urlDatePreset, urlDateFrom, urlDateTo) : {}),
  };

  const organizationsQuery = useQuery({
    retry: false,
    queryKey: ["integrated-organizations", "api", ...Object.values(queries)],
    queryFn: () => organizationservice.getFirsConnected({ ...queries }),
  });

  const { isPending: isLoading, data } = organizationsQuery;

  const organizationData = data?.data;
  const transmissionSummary = organizationData?.summary;

  React.useEffect(() => {
    const filters = organizationData?.filters;
    if (!filters) return;
    setResponseFilters(extractFilterDates(filters));
  }, [organizationData?.filters]);

  const gradientBackground = (from, to) => {
    return `linear-gradient(-5deg, ${from} 0%, ${to} 25%, transparent 25%,transparent 100%)`;
  };

  const stats = {
    totalOrganizationsConnected: transmissionSummary?.totalOrganizationsConnected ?? 0,
    totalApiCalls: transmissionSummary?.totalApiCalls ?? 0,
    totalInvoicesSigned: transmissionSummary?.totalInvoicesSigned ?? 0,
    totalInvoicesSignedSuccessful: transmissionSummary?.totalInvoicesSignedSuccessful ?? 0,
    totalInvoicesSignedFailed: transmissionSummary?.totalInvoicesSignedFailed ?? 0,
    totalTransmissionInitiation: transmissionSummary?.totalTransmissionInitiation ?? 0,
    totalPaymentReported: transmissionSummary?.totalPaymentReported ?? 0,
  };

  function updateSearchParams(mutator) {
    const next = new URLSearchParams(window.location.search);
    mutator(next);
    navigate(
      {
        pathname: window.location.pathname,
        search: next.toString(),
      },
      { replace: true },
    );
  }

  // Live top-level API counters returned by firs-connected (mode=api)
  const columns = [
    {
      key: "name",
      header: "Client",
      render: (_, row) => (
        <Link
          to={{
            pathname: `${pathnames.API_TRANSMISSIONS}/${row?.id}`,
            search: datePreset === DATE_PRESETS.ALL_TIME ? `?datePreset=${DATE_PRESETS.ALL_TIME}` : "",
          }}
          className="fw-medium"
        >
          {row?.name}
        </Link>
      ),
    },
    {
      key: "lastTransmissionDate",
      header: "Last Transmission Date",
      render: (_, row) => {
        const date = row?.lastTransmissionDate ?? row?.lastSyncedAt ?? null;
        return date ? moment(date).format("Do MMM, YYYY, h:mm A") : "N/A";
      },
    },
    { key: "totalApiCalls", header: "Total API Calls", render: (value) => (value ?? 0).toLocaleString() },
    { key: "totalInvoicesSigned", header: "Total Signed", render: (value) => (value ?? 0).toLocaleString() },
    { key: "totalInvoicesSignedSuccessful", header: "Signed Successful", render: (value) => (value ?? 0).toLocaleString() },
    { key: "totalInvoicesSignedFailed", header: "Signed Failed", render: (value) => (value ?? 0).toLocaleString() },
    { key: "totalTransmissionInitiation", header: "Transmission Initiation", render: (value) => (value ?? 0).toLocaleString() },
    { key: "totalPaymentReported", header: "Payment Reported", render: (value) => (value ?? 0).toLocaleString() },
  ];

  function handleChange(e, name = "") {
    const rawValue = name === "page" || name === "limit" ? e : e?.target?.value;
    // Guard against accidentally passing a DOM/event object (e.g. `[object PointerEvent]`) so
    // invalid values never leak into the URL (e.g. `?limit=%5Bobject+PointerEvent%5D`).
    const value =
      rawValue && typeof rawValue === "object" && rawValue?.target?.value != null
        ? rawValue.target.value
        : rawValue;

    updateSearchParams((params) => {
      if (value === "" || value == null) params.delete(name);
      else params.set(name, value);

      if (name !== "page") params.set("page", "1");
    });

    if (name === "page") scrollHandler();
  }

  function handlePresetChange(preset) {
    updateSearchParams((params) => {
      params.set("datePreset", preset);
      params.set("page", "1");
      if (preset !== DATE_PRESETS.CUSTOM) {
        params.delete("dateFrom");
        params.delete("dateTo");
      }
    });
  }

  function handleCustomRangeChange({ dateFrom: from, dateTo: to }) {
    updateSearchParams((params) => {
      params.set("datePreset", DATE_PRESETS.CUSTOM);
      params.set("dateFrom", from);
      params.set("dateTo", to);
      params.set("page", "1");
    });
  }

  return (
    <AppLayout>
      <Header title="FIRS Transmissions" description="Get insights to Firs Transmissions on here" />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <Box className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Box
            sx={{
              display: "inline-flex",
              gap: "8px",
              padding: "6px",
              borderRadius: "14px",
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(pathnames.TRANSMISSIONS)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "10px",
                px: 3,
                color: isFileTransmission ? "#2563EB" : "#6B7280",
                backgroundColor: isFileTransmission ? "#EAF2FF" : "#FFFFFF",
                border: isFileTransmission
                  ? "1.5px solid #2563EB"
                  : "1.5px solid #D1D5DB",
                "&:hover": {
                  backgroundColor: "#EAF2FF",
                },
              }}
            >
              File Transmission
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate(pathnames.API_TRANSMISSIONS)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "10px",
                px: 3,
                color: isApiTransmission ? "#2563EB" : "#6B7280",
                backgroundColor: isApiTransmission ? "#EAF2FF" : "#FFFFFF",
                border: isApiTransmission
                  ? "1.5px solid #2563EB"
                  : "1.5px solid #D1D5DB",
                "&:hover": {
                  backgroundColor: "#EAF2FF",
                },
              }}
            >
              API
            </Button>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <SortFilterMenu
              sortBy={sortBy}
              sortOrder={sortOrder}
              sortByItems={SORT_BY_ITEMS}
              onSortByChange={(e) => handleChange(e, "sortBy")}
              onSortOrderChange={(e) => handleChange(e, "sortOrder")}
            />
            <DateRangePreset
              datePreset={datePreset}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onPresetChange={handlePresetChange}
              onCustomRangeChange={handleCustomRangeChange}
            />
          </Box>
        </Box>

        <div className="row g-3 mb-3">
          {[
            { label: "Total API Calls", value: stats.totalApiCalls?.toLocaleString() || 0, bg: gradientBackground("#a0f2e5", "#0cc6c3") },
            { label: "Total Signed", value: stats.totalInvoicesSigned?.toLocaleString() || 0, bg: gradientBackground("#56ccf2", "#2f80ed") },
            { label: "Transmission Initiation", value: stats.totalTransmissionInitiation?.toLocaleString() || 0, bg: gradientBackground("#fbc2eb", "#a18cd1") },
            { label: "Total Payment Reported", value: stats.totalPaymentReported?.toLocaleString() || 0, bg: gradientBackground("#8e2de2", "#4a00e0") },
          ].map((card) => (
            <div className="col-12 col-lg-3" key={card.label}>
              <Paper className="p-3 h-100" style={{ backgroundColor: "#fff", backgroundImage: card.bg }}>
                <div className="mb-3 text-secondary">{card.label}</div>
                {isLoading ? (
                  <Skeleton variant="text" width="60%" />
                ) : (
                  <div className="fs-4 fw-medium text-break">{card.value}</div>
                )}
              </Paper>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-between align-items-center gap-4 w-100 mb-3">
          <div className="flex-grow-1" style={{ maxWidth: 520 }}>
            <AppTextField
              placeholder="Search transmissions..."
              value={keyword}
              onChange={(e) => handleChange(e, "keyword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <Button
            onClick={() => setIsExport(true)}
            endIcon={<FileDownload />}
            variant="outlined"
            color="secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            Run Report
          </Button>
        </div>
        <DataTable
          data={organizationData?.data || []}
          columns={columns}
          isLoading={isLoading}
          page={Math.max(1, parseInt(page, 10) || 1)}
          pageSize={Math.max(1, parseInt(limit, 10) || 20)}
          totalPages={organizationData?.totalPages || 1}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) => handleChange(value, "limit")}
          selectable={false}
          getRowId={(row) => row.id}
        />
      </div>
      <ExportTransmissionModal
        isOpen={isExport}
        onClose={() => setIsExport(false)}
        mode="api"
        filters={{ keyword: searchTerm, status, sortBy, sortOrder, datePreset, dateFrom, dateTo }}
      />
    </AppLayout>
  );
};

export default TransmissionApiPage;
